// /home/dev/arka_dev/arkadu-ui3/src/components/LoadingScreen.js
import { Box, LinearProgress, Typography } from "@mui/material";
import GlobalStyles from "@mui/material/GlobalStyles";
import { styled } from "@mui/material/styles";
import NProgress from "nprogress";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Animación simplificada con will-change para mejor performance
const floatBg = `
  @keyframes floatBg {
    0%, 100% { transform: translate3d(0,0,0); }
    50% { transform: translate3d(-2%, -2%, 0); }
  }
`;

const Root = styled("div")({
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100dvh",
  background: "linear-gradient(135deg, #f5f7fa 0%, #e8ebf0 100%)",
  overflow: "hidden",
  zIndex: 1300,
});

const AnimatedBackdrop = styled("div")({
  position: "absolute",
  inset: "-10%",
  background:
    "repeating-linear-gradient(135deg, rgba(100,116,139,0.03) 0px, rgba(100,116,139,0.03) 2px, transparent 2px, transparent 10px)",
  animation: "floatBg 14s ease-in-out infinite",
  willChange: "transform",
  pointerEvents: "none",
});

const Card = styled(Box)({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
  maxWidth: 560,
  padding: 24,
  borderRadius: 16,
  border: "1px solid rgba(226,232,240,0.8)",
  background: "rgba(255,255,255,0.85)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
  "@media (min-width: 600px)": { padding: 32 },
});

const AccentBar = styled("div")({
  height: 4,
  width: "100%",
  borderRadius: 999,
  background: "linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)",
});

const StyledLinear = styled(LinearProgress)({
  height: 8,
  borderRadius: 999,
  backgroundColor: "rgba(59,130,246,0.12)",
  overflow: "hidden",
  "& .MuiLinearProgress-bar": {
    borderRadius: 999,
    background: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
    willChange: "transform",
  },
});

const LoadingScreen = React.memo(function LoadingScreen({ message, ...rest }) {
  const { t } = useTranslation();

  useEffect(() => {
    NProgress.configure({
      showSpinner: false,
      trickleSpeed: 120,
      minimum: 0.1,
    });
    NProgress.start();
    return () => NProgress.done();
  }, []);

  const text = useMemo(
    () =>
      message && typeof message === "string"
        ? message
        : "Loading, please wait...",
    [message],
  );

  return (
    <Root role="alert" aria-busy="true" aria-live="polite" {...rest}>
      <GlobalStyles
        styles={{
          [floatBg]: {},
          "#nprogress .bar": { background: "#3b82f6", height: 3 },
          "#nprogress .peg": {
            boxShadow:
              "0 0 10px rgba(59,130,246,0.6), 0 0 5px rgba(59,130,246,0.4)",
          },
        }}
      />
      <AnimatedBackdrop />
      <Card>
        <AccentBar />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: "#1e293b",
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
            }}
          >
            {t("Cargando...")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontSize: { xs: 11, sm: 12 },
            }}
          >
            {t("Optimizando la experiencia para ti...")}
          </Typography>
        </Box>

        <StyledLinear color="primary" />

        <Typography
          variant="body2"
          sx={{
            mt: 0.5,
            color: "#475569",
            fontSize: { xs: 13, sm: 14 },
          }}
        >
          {text}
        </Typography>
      </Card>
    </Root>
  );
});

export default LoadingScreen;
